import React, { useState, useEffect } from 'react';
import { HardwareTypeCreate } from '../../services/typesService';
import { clientService } from '../../services/clientService';
import { Client } from '../../types/client';
import { useI18n } from '../../contexts/I18nContext';

interface AddHardwareTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  onCreate: (data: HardwareTypeCreate) => Promise<void>;
}

interface FormErrors {
  [key: string]: string;
}

/**
 * A new hardware type: the client it belongs to, its code and commercial
 * name, then the optional description and reboot command.
 */
export const AddHardwareTypeModal: React.FC<AddHardwareTypeModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  onCreate
}) => {
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [formData, setFormData] = useState<HardwareTypeCreate>({
    code: '',
    name: '',
    description: '',
    reboot_payload: '',
    client_id: ''
  });

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingClients(true);
    clientService.fetchActiveClients()
      .then((list) => { if (!cancelled) setClients(list); })
      .catch((error) => { console.error('Failed to fetch clients:', error); })
      .finally(() => { if (!cancelled) setLoadingClients(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.client_id) newErrors.client_id = t('deviceTypes.validation.clientRequired');
    if (!formData.code.trim()) newErrors.code = t('deviceTypes.validation.codeRequired');
    else if (!/^[A-Z0-9]{2,10}$/.test(formData.code)) newErrors.code = t('deviceTypes.validation.codeFormatCreate');
    if (!formData.name.trim()) newErrors.name = t('deviceTypes.validation.nameRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof HardwareTypeCreate, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await onCreate(formData);
      onCreated();
      resetForm();
      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail
        || (error instanceof Error ? error.message : t('deviceTypes.create.failed'));
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ code: '', name: '', description: '', reboot_payload: '', client_id: '' });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const clientName = clients.find((c) => c.id === formData.client_id)?.name;
  const summaryReady = Boolean(formData.client_id && formData.code.trim() && formData.name.trim());

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="config-template-modal tpc dtm" data-testid="modal-add-hardware-type" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title tpd-title">
            <div className="tpd-title-text">
              <h2>{t('deviceTypes.addHardwareType')}</h2>
              <div className="tpd-title-sub"><span>{t('deviceTypes.create.subtitle')}</span></div>
            </div>
          </div>
          <button type="button" className="close-button" data-testid="btn-modal-close" onClick={handleClose} aria-label={t('common.close')}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="tpc-form">
          <div className="modal-content tpd-body">
            {errors.submit && <div className="error-message" role="alert">{errors.submit}</div>}

            <section className="cu-section">
              <header className="cu-section-head">
                <h3>{t('clients.client')}</h3>
                <p>{t('deviceTypes.create.clientIntro')}</p>
              </header>
              <div className="cu-field">
                <label htmlFor="hwtClient">{t('clients.client')} *</label>
                <select
                  id="hwtClient"
                  value={formData.client_id}
                  data-testid="field-client-id"
                  onChange={(e) => handleInputChange('client_id', e.target.value)}
                  className={`cu-input${errors.client_id ? ' is-invalid' : ''}`}
                  disabled={isSubmitting || loadingClients}
                >
                  <option value="">{loadingClients ? t('configTemplates.ownerLoadingClients') : t('deviceTypes.create.pickClient')}</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
                {errors.client_id ? <span className="cu-error">{errors.client_id}</span> : <span className="cu-hint">{t('deviceTypes.clientOrganizationHelp')}</span>}
              </div>
            </section>

            <section className="cu-section">
              <header className="cu-section-head">
                <h3>{t('deviceTypes.create.identity')}</h3>
                <p>{t('deviceTypes.create.identityIntro')}</p>
              </header>
              <div className="cu-grid">
                <div className="cu-field">
                  <label htmlFor="hwtCode">{t('deviceTypes.deviceTypeCode')} *</label>
                  <input
                    type="text"
                    id="hwtCode"
                    value={formData.code}
                    data-testid="field-hardware-type-code"
                    onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                    className={`cu-input mono${errors.code ? ' is-invalid' : ''}`}
                    placeholder={t('deviceTypes.create.codePlaceholder')}
                    maxLength={10}
                    disabled={isSubmitting}
                  />
                  {errors.code ? <span className="cu-error">{errors.code}</span> : <span className="cu-hint">{t('deviceTypes.create.codeHint')}</span>}
                </div>
                <div className="cu-field">
                  <label htmlFor="hwtName">{t('deviceTypes.commercialName')} *</label>
                  <input
                    type="text"
                    id="hwtName"
                    value={formData.name}
                    data-testid="field-hardware-type-name"
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`cu-input${errors.name ? ' is-invalid' : ''}`}
                    placeholder={t('deviceTypes.create.namePlaceholder')}
                    disabled={isSubmitting}
                  />
                  {errors.name ? <span className="cu-error">{errors.name}</span> : <span className="cu-hint">{t('deviceTypes.commercialNameHelp')}</span>}
                </div>
              </div>
            </section>

            <section className="cu-section">
              <header className="cu-section-head">
                <h3>{t('deviceTypes.create.details')}</h3>
                <p>{t('deviceTypes.create.detailsIntro')}</p>
              </header>
              <div className="cu-grid">
                <div className="cu-field cu-field-wide">
                  <label htmlFor="hwtDescription">{t('common.description')}</label>
                  <textarea
                    id="hwtDescription"
                    value={formData.description || ''}
                    data-testid="field-hardware-type-description"
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="cu-input"
                    placeholder={t('deviceTypes.descriptionPlaceholder')}
                    rows={2}
                    disabled={isSubmitting}
                  />
                  <span className="cu-hint">{t('deviceTypes.create.descriptionHint')}</span>
                </div>
                <div className="cu-field cu-field-wide">
                  <label htmlFor="hwtReboot">{t('deviceTypes.rebootPayload')}</label>
                  <input
                    type="text"
                    id="hwtReboot"
                    value={formData.reboot_payload || ''}
                    data-testid="field-hardware-type-reboot-payload"
                    onChange={(e) => handleInputChange('reboot_payload', e.target.value)}
                    className="cu-input mono"
                    placeholder={t('deviceTypes.create.rebootPlaceholder')}
                    maxLength={16}
                    disabled={isSubmitting}
                  />
                  <span className="cu-hint">{t('deviceTypes.rebootPayloadHelp')}</span>
                </div>
              </div>
            </section>
          </div>

          <div className="modal-actions tpd-footer tpc-footer">
            <div className="tpc-summary">
              {summaryReady ? (
                <>
                  <strong>{formData.name.trim()}</strong>
                  <span className="type-badge tpl-type">{formData.code}</span>
                  {clientName && <span className="tpc-summary-owner">{clientName}</span>}
                </>
              ) : (
                <span className="tpd-muted">{t('deviceTypes.create.summaryEmpty')}</span>
              )}
            </div>
            <div className="tpc-actions">
              <button type="button" onClick={handleClose} className="btn btn-secondary" data-testid="btn-cancel" disabled={isSubmitting}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" data-testid="btn-submit" disabled={isSubmitting}>
                {isSubmitting ? t('deviceTypes.create.creating') : t('deviceTypes.create.submit')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
